import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';

type TrustContext = 'asset' | 'media' | 'frame' | 'navigation' | 'script' | 'connect';

@Injectable({
  providedIn: 'root'
})
export class TrustedResourceService {
  private readonly security = environment.security;

  cdnUrl(path: string): string {
    const normalizedPath = String(path ?? '').replace(/^\/+/, '');
    return `${this.security.cdnBaseUrl.replace(/\/$/, '')}/${normalizedPath}`;
  }

  isTrustedUrl(candidate: string | null | undefined, context: TrustContext, allowRelative = false): boolean {
    const value = String(candidate ?? '').trim();
    if (!value) {
      return false;
    }

    if (allowRelative && this.isSafeRelativeUrl(value)) {
      return true;
    }

    const parsed = this.toUrl(value);
    if (!parsed) {
      return false;
    }

    if (this.isCurrentOrigin(parsed)) {
      return true;
    }

    if (parsed.protocol !== 'https:') {
      return false;
    }

    const allowedHosts = this.getAllowedHosts(context);
    return allowedHosts.some((pattern) => this.matchesHost(parsed.hostname, pattern));
  }

  resolveAssetUrl(candidate: string | null | undefined, fallbackPath: string): string {
    if (this.isTrustedUrl(candidate, 'asset', true)) {
      return this.toAbsoluteUrl(candidate as string);
    }

    return this.cdnUrl(fallbackPath);
  }

  resolveMediaUrl(candidate: string | null | undefined, fallbackPath: string): string {
    if (this.isTrustedUrl(candidate, 'media', true)) {
      return this.toAbsoluteUrl(candidate as string);
    }

    return this.cdnUrl(fallbackPath);
  }

  resolveFrameUrl(candidate: string | null | undefined): string | null {
    if (!this.isTrustedUrl(candidate, 'frame')) {
      return null;
    }

    return this.toAbsoluteUrl(candidate as string);
  }

  resolveNavigationUrl(candidate: string | null | undefined, allowRelative = false): string | null {
    if (!this.isTrustedUrl(candidate, 'navigation', allowRelative)) {
      return null;
    }

    return this.toAbsoluteUrl(candidate as string);
  }

  resolveInternalRoute(candidate: string | null | undefined): string | null {
    const value = String(candidate ?? '').trim();
    if (!this.isSafeRelativeUrl(value) || !value.startsWith('/')) {
      return null;
    }

    return value;
  }

  sanitizeDisplayText(value: unknown, fallback = 'Content unavailable', maxLength = 180): string {
    const normalized = String(value ?? '')
      .replace(/[<>]/g, '')
      .replace(/[\u0000-\u001F\u007F]/g, ' ')
      .replace(/\s+/g, ' ')
      .trim();

    if (!normalized) {
      return fallback;
    }

    if (normalized.length <= maxLength) {
      return normalized;
    }

    return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
  }

  private getAllowedHosts(context: TrustContext): string[] {
    switch (context) {
      case 'asset':
        return this.security.trustedAssetHosts;
      case 'media':
        return this.security.trustedMediaHosts;
      case 'frame':
        return this.security.trustedFrameHosts;
      case 'navigation':
        return this.security.trustedNavigationHosts;
      case 'script':
        return this.security.trustedScriptHosts;
      case 'connect':
        return this.security.trustedConnectHosts;
      default:
        return [];
    }
  }

  private isSafeRelativeUrl(value: string): boolean {
    if (!value) {
      return false;
    }

    if (/^(?:[a-z]+:|\/\/)/i.test(value)) {
      return false;
    }

    return !/(javascript:|data:|vbscript:)/i.test(value);
  }

  private toUrl(value: string): URL | null {
    try {
      return new URL(value, this.getBaseOrigin());
    } catch {
      return null;
    }
  }

  private toAbsoluteUrl(value: string): string {
    const parsed = this.toUrl(value);
    return parsed ? parsed.href : value;
  }

  private isCurrentOrigin(url: URL): boolean {
    const currentOrigin = this.getBaseOrigin();
    return !!currentOrigin && url.origin === currentOrigin;
  }

  private getBaseOrigin(): string {
    if (typeof globalThis !== 'undefined' && globalThis.location?.origin) {
      return globalThis.location.origin;
    }

    return 'https://m.billing.bitbridge.co.za';
  }

  private matchesHost(hostname: string, pattern: string): boolean {
    if (!pattern) {
      return false;
    }

    if (pattern.startsWith('*.')) {
      const suffix = pattern.slice(2);
      return hostname === suffix || hostname.endsWith(`.${suffix}`);
    }

    return hostname === pattern;
  }
}
